import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import {
  FetchProductSearchResultsRequest,
  FetchProductSearchResultsResponse,
  ProductSearchFilters,
  SearchResultProduct,
} from "@/modules/productStorefront/features/ProductDirectory/api/fetchProductSearchResults/types";
import {
  FetchProductDetailsResponse,
  ProductDetails,
} from "@/modules/productStorefront/features/ProductDirectory/api/fetchProductDetails/types";
import { fetchProductSearchResults } from "@/modules/productStorefront/features/ProductDirectory/api/fetchProductSearchResults/fetchProductSearchResultsApi";
import { fetchProductDetails } from "@/modules/productStorefront/features/ProductDirectory/api/fetchProductDetails/fetchProductDetailsApi";

interface ProductDirectoryState {
  searchData: SearchResultProduct[];
  searchFilters: ProductSearchFilters;
  searchTotalCount: number;
  fetchSearchLoading: boolean;
  fetchSearchError: string | null;
  productDetails: ProductDetails | null;
  fetchDetailsLoading: boolean;
  fetchDetailsError: string | null;
}

const initialState: ProductDirectoryState = {
  searchData: [],
  searchFilters: {
    categories: [],
  },
  searchTotalCount: 0,
  fetchSearchLoading: false,
  fetchSearchError: null,
  productDetails: null,
  fetchDetailsLoading: false,
  fetchDetailsError: null,
};

const productDirectorySlice = createSlice({
  name: "productDirectory",
  initialState,
  reducers: {
    setFetchSearchLoading(state, action: PayloadAction<boolean>) {
      state.fetchSearchLoading = action.payload;
    },
    setFetchSearchError(state, action: PayloadAction<string | null>) {
      state.fetchSearchError = action.payload;
    },
    setSearchData(state, action: PayloadAction<SearchResultProduct[]>) {
      state.searchData = action.payload;
    },
    appendSearchData(state, action: PayloadAction<SearchResultProduct[]>) {
      state.searchData = [...state.searchData, ...action.payload];
    },
    setSearchFilters(
      state,
      action: PayloadAction<ProductDirectoryState["searchFilters"]>,
    ) {
      state.searchFilters = action.payload;
    },
    setSearchTotalCount(state, action: PayloadAction<number>) {
      state.searchTotalCount = action.payload;
    },
    setFetchDetailsLoading(state, action: PayloadAction<boolean>) {
      state.fetchDetailsLoading = action.payload;
    },
    setFetchDetailsError(state, action: PayloadAction<string | null>) {
      state.fetchDetailsError = action.payload;
    },
    setProductDetails(state, action: PayloadAction<ProductDetails | null>) {
      state.productDetails = action.payload;
    },
  },
});

export default productDirectorySlice.reducer;

export const {
  setFetchSearchLoading,
  setFetchSearchError,
  setSearchData,
  appendSearchData,
  setSearchFilters,
  setSearchTotalCount,
  setFetchDetailsLoading,
  setFetchDetailsError,
  setProductDetails,
} = productDirectorySlice.actions;

export const fetchProductSearchResultsAction =
  (params: FetchProductSearchResultsRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    if (!params.page || params.page === 1) {
      dispatch(setFetchSearchLoading(true));
    }
    dispatch(setFetchSearchError(null));
    try {
      const response: FetchProductSearchResultsResponse =
        await fetchProductSearchResults(params);

      if (params.page && params.page > 1) {
        dispatch(appendSearchData(response.data.products));
      } else {
        dispatch(setSearchData(response.data.products));
      }

      dispatch(setSearchFilters(response.data.filters));
      dispatch(setSearchTotalCount(response.data.totalCount));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch product search results.";
      dispatch(setFetchSearchError(message));
    } finally {
      dispatch(setFetchSearchLoading(false));
    }
  };

export const fetchProductDetailsAction =
  (uuid: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setFetchDetailsLoading(true));
    dispatch(setFetchDetailsError(null));
    try {
      const response: FetchProductDetailsResponse =
        await fetchProductDetails(uuid);
      dispatch(setProductDetails(response.data));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch product details.";
      dispatch(setFetchDetailsError(message));
    } finally {
      dispatch(setFetchDetailsLoading(false));
    }
  };
